import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';

export default function DateFilter({ value, onChange, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const presets = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'This Quarter', value: 'quarter' },
    { label: 'This Year', value: 'year' },
    { label: 'Custom Range', value: 'custom' },
  ];

  const handlePresetClick = (preset) => {
    if (preset.value === 'custom') {
      setShowCustomPicker(true);
    } else {
      onChange(preset.value);
      setShowCustomPicker(false);
      setIsOpen(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (customRange.from && customRange.to) {
      onChange({
        type: 'custom',
        from: format(customRange.from, 'yyyy-MM-dd'),
        to: format(customRange.to, 'yyyy-MM-dd')
      });
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setCustomRange({ from: null, to: null });
    setShowCustomPicker(false);
    setIsOpen(false);
  };

  const getDisplayLabel = () => {
    if (!value) return 'All Time';
    
    if (typeof value === 'object' && value.type === 'custom') {
      return `${value.from} to ${value.to}`;
    }
    
    const preset = presets.find(p => p.value === value);
    return preset ? preset.label : 'All Time';
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className={`justify-start text-left font-normal ${className}`}
          data-testid="date-filter-button"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <span>{getDisplayLabel()}</span>
          {value && (
            <X 
              className="ml-2 h-3 w-3 hover:text-destructive" 
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b">
          <p className="text-sm font-semibold">Filter by Date</p>
        </div>
        
        {!showCustomPicker ? (
          <div className="p-2">
            {presets.map((preset) => (
              <Button
                key={preset.value}
                variant={value === preset.value ? 'secondary' : 'ghost'}
                className="w-full justify-start mb-1"
                onClick={() => handlePresetClick(preset)}
                data-testid={`date-filter-${preset.value}`}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        ) : (
          <div className="p-3 space-y-3">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Select Date Range</p>
              <Calendar
                mode="range"
                selected={customRange}
                onSelect={setCustomRange}
                numberOfMonths={2}
                data-testid="date-filter-calendar"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => setShowCustomPicker(false)}
              >
                Back
              </Button>
              <Button 
                size="sm" 
                className="flex-1"
                onClick={handleCustomRangeApply}
                disabled={!customRange.from || !customRange.to}
                data-testid="date-filter-apply"
              >
                Apply
              </Button>
            </div>
          </div>
        )}
        
        {value && !showCustomPicker && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-destructive hover:text-destructive"
              onClick={handleClear}
              data-testid="date-filter-clear"
            >
              Clear Filter
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
